<script setup lang="ts">
import BaseModal from '@/components/modal/BaseModal.vue';
import ModalFooterConfirmButtons from '@/components/modal/ModalFooterConfirmButtons.vue';
import { COLOR_CLASSES } from '@/constants/colors';
import { useModalStore } from '@/stores/modal';
import { ref, watch } from 'vue';

interface Props {
  isOpen: boolean;
  initialContent?: string;
  mode: 'create' | 'edit';
  mindShareId?: number;
  onSubmit: (content: string, mode: 'create' | 'edit', id?: number) => void;
}

interface Emits {
  (e: 'close'): void;
  (e: 'submit', content: string, mode: 'create' | 'edit', id?: number): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const modalStore = useModalStore();
const content = ref(props.initialContent || '');
const isSubmitting = ref(false);

watch(
  () => props.initialContent,
  newContent => {
    if (newContent !== undefined) {
      content.value = newContent;
    }
  },
);

const handleSubmit = () => {
  if (!content.value) return;

  isSubmitting.value = true;

  const replacedContent = content.value.replace(/\n/g, '\\n');
  props.onSubmit(replacedContent, props.mode, props.mindShareId);

  // 약간의 지연 후 로딩 상태 해제
  setTimeout(() => {
    isSubmitting.value = false;
  }, 300);
};

const handleClose = () => {
  modalStore.close();
};
</script>

<template>
  <BaseModal title="내 마음 나누기" width="360px" @close="handleClose">
    <div class="flex flex-col gap-1">
      <textarea
        v-model="content"
        class="w-full h-[200px] px-2 py-3 border border-[#EAEAEA] rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent max-h-[122px] font-normal text-base leading-[113%] tracking-[-0.33px] rounded-sm"
        placeholder="법문을 듣고 나눠주시는 마음을 적어주세요."
        maxlength="500"
      />
      <p class="text-[13px] text-right" :class="[COLOR_CLASSES.text.gray]">{{ content.length }}/500자</p>
    </div>

    <template #footer>
      <ModalFooterConfirmButtons
        cancel-text="취소"
        :confirm-text="mode === 'create' ? '등록하기' : '수정하기'"
        :is-loading="isSubmitting"
        :disabled="!content || (props.mode === 'edit' && content === props.initialContent)"
        @cancel="emit('close')"
        @confirm="handleSubmit"
      />
    </template>
  </BaseModal>
</template>
